import { AIRLINES, clockFromTime, MAP_THEMES } from '@am/sim';
import { type FormEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { setMenuBack } from '../back-button';
import { type OpenRoom, openRooms, type ServerStats, serverStats, titleOf } from '../community';
import { newGameSave } from '../demo';
import { APP_VERSION } from '../diagnostics';
import { locked, offerFullVersion } from '../full-version';
import { localeOf, type MessageKey, translate, useT } from '../i18n';
import { canLoad, listSaves, type SaveMeta } from '../saves';
import { hideSplash } from '../splash';
import { randomAirportName, type StartMode, savePrefs, sessionDefaults, useGame } from '../store';
import { AccountPage } from './AccountPage';
import { LOGO_URL } from './art';
import { LeaderboardPage, ProfilePage, TitleChip } from './Community';
import { InstallHint } from './InstallHint';
import { Icon, type IconName } from './icons';
import { LanguageSwitch } from './LanguageSwitch';
import { SavesPage, startSave } from './SavesPage';
import { SettingsContent } from './Settings';
import { WhatsNew } from './WhatsNew';

type Page =
  | 'home'
  | 'single'
  | 'saves'
  | 'coop'
  | 'leaderboard'
  | 'profile'
  | 'settings'
  | 'account';

/** Pages that need an account (without one, the account page opens instead, saying why). */
const NEEDS_ACCOUNT = new Set<Page>(['coop', 'leaderboard', 'profile']);

/**
 * Pictures of the game behind the title screen, one after another (stills, not the live game:
 * the menu stays light and opens at once, and the game's graphics start only when playing).
 */
const SLIDES = ['/menu/1-day.jpg', '/menu/2-sunset.jpg', '/menu/3-night.jpg', '/menu/4-apron.jpg'];
/** Seconds each picture shows. */
const SLIDE_SECONDS = 8;
/**
 * Films shot in the game's engine at the highest settings (film/director.ts), played in a loop
 * with a crossfade. The stills stand in on the potato profile, with reduced motion, or when a
 * film can't play.
 */
const FILMS = [
  'stealth',
  'night',
  'f35',
  'giant',
  'road',
  'escort',
  'apron',
  'jumbo',
  'display',
  'military',
  'dawn',
];
/**
 * The films' format here: VP9 WebM where it plays, else the H.264 MP4 copies (the iPhone and
 * iPad app's web view plays no WebM), else none and the stills stay. The phone and tablet apps'
 * build (iOS and Android alike) ships only the MP4s, the Windows game only the WebMs, the web
 * both: in the app it is MP4 even where WebM would play.
 */
const FILM_EXT = ((): string | null => {
  const video = document.createElement('video');
  const app = import.meta.env.MODE === 'app';
  if (!app && video.canPlayType('video/webm; codecs="vp9"') !== '') return 'webm';
  if (video.canPlayType('video/mp4; codecs="avc1.640029"') !== '') return 'mp4';
  return null;
})();
const filmSrc = (i: number) => `/menu/film/${FILMS[i % FILMS.length]}.${FILM_EXT}`;
const filmPoster = (i: number) => `/menu/film/${FILMS[i % FILMS.length]}.jpg`;
/** Seconds of crossfade between two films. */
const FILM_FADE = 1.2;

/**
 * Whether the films can play here. They play whatever the graphics preset or the system's
 * reduced-motion setting (Windows turns that on with its animation effects off, and players
 * took the stills for the old menu).
 */
function wantsFilm(): boolean {
  return FILM_EXT !== null;
}

/**
 * Title screen: pictures of the airport behind, the menu on the left, sub-pages sliding in on
 * the right and a departures board ticking over in the corner.
 */
export function MainMenu() {
  const t = useT();
  const lang = useGame((s) => s.lang);
  const menuError = useGame((s) => s.menuError);
  const account = useGame((s) => s.account);
  const [slide, setSlide] = useState(0);
  const [film, setFilm] = useState(wantsFilm);
  const filmShown = useRef(filmPoster(0));
  const [page, setPage] = useState<Page>(menuError ? 'coop' : 'home');
  /** The feature that sent a player without an account to the account page. */
  const [needs, setNeeds] = useState<'coop' | 'leaderboard' | 'profile' | null>(null);
  /** A player opened from the leaderboard. */
  const [viewing, setViewing] = useState<string | null>(null);
  const [stats, setStats] = useState<ServerStats | null>(null);
  useEffect(() => {
    if (!account) return;
    let alive = true;
    const load = () => {
      serverStats()
        .then((s) => alive && setStats(s))
        .catch(() => undefined);
    };
    load();
    const id = window.setInterval(load, 30_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [account]);
  // The newest save, for "Continue".
  const [latest, setLatest] = useState<SaveMeta | null>(null);
  const refreshLatest = () => {
    listSaves()
      .then((all) => setLatest(all.find(canLoad) ?? null))
      .catch(() => setLatest(null));
  };
  // The saves: the account's from the server, or this device's without one.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reload when the account changes
  useEffect(refreshLatest, [account]);
  // Signed out: back to the start, nothing of the previous player's left open.
  useEffect(() => {
    if (!account) setPage('home');
  }, [account]);
  const open = (next: Page) => {
    setViewing(null);
    if (!account && NEEDS_ACCOUNT.has(next)) {
      setNeeds(next as 'coop' | 'leaderboard' | 'profile');
      setPage('account');
      return;
    }
    setNeeds(null);
    setPage(page === next ? 'home' : next);
  };

  useEffect(() => {
    hideSplash();
    if (film) return;
    const timer = window.setInterval(
      () => setSlide((n) => (n + 1) % SLIDES.length),
      SLIDE_SECONDS * 1000,
    );
    return () => window.clearInterval(timer);
  }, [film]);

  // The phone's back button (back-button.ts): back to the main list from a page.
  useEffect(() => {
    setMenuBack(() => {
      if (page === 'home') return false;
      setPage('home');
      return true;
    });
    return () => setMenuBack(null);
  }, [page]);

  // Esc goes back to the main list.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPage('home');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const start = (startMode: StartMode) => {
    const state = useGame.getState();
    // Signed in, players go by their account name (so others see their title too).
    const name =
      state.account?.username ?? (state.playerName.trim() || translate(lang, 'menu.defaultPlayer'));
    savePrefs({ ...state, playerName: name });
    useGame.setState({
      ...sessionDefaults,
      // The picture behind the menu stays behind the loading screen.
      loadingImage: film ? filmShown.current : (SLIDES[slide] ?? state.loadingImage),
      playerName: name,
      screen: 'game',
      startMode,
      menuError: null,
    });
  };

  const items: {
    page: Page | 'gallery' | 'continue' | 'academy' | 'legal';
    icon: IconName;
    label: MessageKey;
    sub: string;
  }[] = [
    ...(latest
      ? [
          {
            page: 'continue' as const,
            icon: 'play' as IconName,
            label: 'menu.continue' as MessageKey,
            // The autosave's name was written in the language of the day: said in today's.
            sub: `${latest.auto ? t('save.autoName') : latest.name} · ${t('hud.day', { day: clockFromTime(latest.time).day })}`,
          },
        ]
      : []),
    { page: 'single', icon: 'plane', label: 'menu.singleplayer', sub: t('menu.singleSub') },
    { page: 'saves', icon: 'save', label: 'menu.saves', sub: t('menu.savesSub') },
    { page: 'coop', icon: 'users', label: 'menu.coop', sub: t('menu.coopSub') },
    {
      page: 'leaderboard',
      icon: 'trophy',
      label: 'menu.leaderboard',
      sub: t('menu.leaderboardSub'),
    },
    { page: 'profile', icon: 'crown', label: 'menu.profile', sub: t('menu.profileSub') },
    { page: 'academy', icon: 'star', label: 'menu.academy', sub: t('menu.academySub') },
    { page: 'settings', icon: 'gear', label: 'menu.settings', sub: t('menu.settingsSub') },
    { page: 'gallery', icon: 'grid', label: 'menu.gallery', sub: t('menu.gallerySub') },
    { page: 'legal', icon: 'doc', label: 'menu.legal', sub: t('menu.legalSub') },
  ];

  return (
    <div className="title">
      {film ? (
        <TitleFilm
          onShot={(poster) => {
            filmShown.current = poster;
          }}
          onFail={() => setFilm(false)}
        />
      ) : (
        <div className="title-slides" aria-hidden="true">
          {SLIDES.map((src, i) => (
            <img
              key={src}
              src={src}
              alt=""
              draggable={false}
              className={i === slide ? 'shown' : ''}
              // Only the first one at once; the others when the browser has time.
              loading={i === 0 ? 'eager' : 'lazy'}
            />
          ))}
        </div>
      )}
      <div className="title-shade" />

      <aside className="title-side">
        <div className="brand">
          <img className="brand-logo" src={LOGO_URL} alt="Airport Manager 2027" draggable={false} />
        </div>
        <p className="brand-tagline">{t('menu.subtitle')}</p>

        <nav className="title-nav">
          {items.map((item, i) => (
            <button
              type="button"
              key={item.page}
              className={`title-item ${page === item.page ? 'active' : ''}`}
              style={{ animationDelay: `${120 + i * 70}ms` }}
              onClick={() => {
                if (item.page === 'gallery') location.href = '/?gallery';
                else if (item.page === 'legal') useGame.setState({ legal: 'about' });
                else if (item.page === 'academy')
                  useGame.setState({ helpOpen: true, academyArticle: null });
                else if (item.page === 'continue') {
                  if (latest) void startSave(latest, 'offline', start).catch(refreshLatest);
                } else open(item.page);
              }}
            >
              <Icon name={item.icon} size={22} />
              <span className="title-item-text">
                <span className="title-item-label">{t(item.label)}</span>
                <span className="title-item-sub">{item.sub}</span>
              </span>
              {!account && NEEDS_ACCOUNT.has(item.page as Page) ? (
                <Icon name="lock" size={16} className="title-item-lock" />
              ) : (
                <Icon name="chevron" size={18} className="title-item-arrow" />
              )}
            </button>
          ))}
        </nav>

        {/* Where the games are kept: this device, or the account (every device). */}
        <button
          type="button"
          className={`account-card ${account ? 'on' : ''} ${page === 'account' ? 'active' : ''}`}
          onClick={() => {
            setNeeds(null);
            setPage(page === 'account' ? 'home' : 'account');
          }}
        >
          <Icon name={account ? 'cloud' : 'phone'} size={20} />
          <span className="account-card-text">
            <strong>{account ? account.username : t('account.cardLocal')}</strong>
            <span>{t(account ? 'account.cardSynced' : 'account.cardSync')}</span>
          </span>
          <Icon name="chevron" size={16} />
        </button>

        {page === 'home' && <InstallHint />}
        {account && stats && (
          <p className="title-online small">
            <span className="live-dot" />
            {t('menu.online', { ...stats })}
          </p>
        )}
        <footer className="title-foot">
          <LanguageSwitch />
          {/* The release (the desktop app's version; 'dev' while developing). */}
          <span>v{APP_VERSION}</span>
        </footer>
      </aside>

      {page !== 'home' && (
        <section
          className="title-sheet"
          key={page}
          aria-label={t(`menu.${page}Title` as MessageKey)}
        >
          <header className="sheet-head">
            <button type="button" className="icon-btn" onClick={() => setPage('home')}>
              <Icon name="back" size={18} />
            </button>
            <h2>{t(`menu.${page}Title` as MessageKey)}</h2>
          </header>
          {page === 'single' && (
            <SinglePage
              onStart={(airportName, demo, large, theme) => {
                // The large map comes with the full version (the free Android game); every
                // landscape is free.
                if (large && !demo && locked()) {
                  offerFullVersion('map');
                  return;
                }
                const save = newGameSave(airportName, demo, large, theme);
                start(
                  save
                    ? { kind: 'offline', save, fresh: true }
                    : { kind: 'offline', airportName, fresh: true },
                );
              }}
            />
          )}
          {page === 'saves' && <SavesPage onStart={start} onChange={refreshLatest} />}
          {page === 'coop' && <CoopPage onStart={start} />}
          {page === 'leaderboard' &&
            (viewing ? (
              <ProfilePage username={viewing} onBack={() => setViewing(null)} />
            ) : (
              <LeaderboardPage onProfile={setViewing} />
            ))}
          {page === 'profile' && <ProfilePage />}
          {page === 'settings' && <SettingsPage />}
          {page === 'account' && <AccountPage reason={needs} onSignedIn={refreshLatest} />}
        </section>
      )}

      {menuError && page !== 'coop' && (
        <div className="title-error" role="alert">
          <Icon name="warning" size={18} />
          {translate(lang, menuError.key, menuError.params)}
        </div>
      )}

      <DeparturesBoard />
      <WhatsNew />
    </div>
  );
}

function NameField() {
  const t = useT();
  const playerName = useGame((s) => s.playerName);
  return (
    <label className="field">
      <span>{t('menu.name')}</span>
      <input
        value={playerName}
        maxLength={24}
        onChange={(e) => useGame.setState({ playerName: e.target.value })}
        placeholder={t('menu.defaultPlayer')}
        autoComplete="nickname"
      />
    </label>
  );
}

function Feature({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <li>
      <Icon name={icon} size={18} />
      <span>{children}</span>
    </li>
  );
}

/** The new airport's name, with a die for another idea. */
function AirportNameField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const t = useT();
  return (
    <label className="field">
      <span>{t('menu.airportName')}</span>
      <div className="join-row">
        <input value={value} maxLength={32} onChange={(e) => onChange(e.target.value)} />
        <button
          type="button"
          className="icon-btn"
          title={t('menu.airportNameRandom')}
          onClick={() => onChange(randomAirportName(value))}
        >
          <Icon name="dice" size={18} />
        </button>
      </div>
    </label>
  );
}

/** Start on empty land or on the ready-made demo airport. */
function StartKind({ demo, onChange }: { demo: boolean; onChange: (demo: boolean) => void }) {
  const t = useT();
  return (
    <div className="field">
      <span>{t('menu.startKind')}</span>
      <div className="start-kinds">
        {[false, true].map((d) => (
          <button
            key={String(d)}
            type="button"
            className={`start-kind ${d === demo ? 'active' : ''}`}
            aria-pressed={d === demo}
            onClick={() => onChange(d)}
          >
            <Icon name={d ? 'plane' : 'build'} size={18} />
            <span>
              <strong>{t(d ? 'menu.start.demo' : 'menu.start.empty')}</strong>
              <span className="muted small">
                {t(d ? 'menu.start.demoSub' : 'menu.start.emptySub')}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** The landscapes, each with a glimpse of its horizon (art/horizon) under its own sky. */
const THEME_PICTURES = [
  'url(/art/horizon/horizon-city.png), linear-gradient(#86b2dc, #d3e3f0)',
  'url(/art/horizon/horizon-desert-city.png), linear-gradient(#c9b089, #f1dfbd)',
  'url(/art/horizon/horizon-alpine-village.png), linear-gradient(#5f93d0, #d2e4f5)',
];

/** The landscape the airport is built in: the green valley, the desert or the mountains. */
function MapThemeField({ theme, onChange }: { theme: number; onChange: (theme: number) => void }) {
  const t = useT();
  return (
    <div className="field">
      <span>{t('menu.mapTheme')}</span>
      <div className="map-themes">
        {MAP_THEMES.map((id) => (
          <button
            key={id}
            type="button"
            className={`map-theme map-theme-${id} ${id === theme ? 'active' : ''}`}
            aria-pressed={id === theme}
            onClick={() => onChange(id)}
          >
            <span
              className="map-theme-picture"
              style={{ backgroundImage: THEME_PICTURES[id] }}
              aria-hidden="true"
            />
            <strong>{t(`menu.mapTheme.${id}` as MessageKey)}</strong>
            <span className="muted small">{t(`menu.mapTheme.${id}.sub` as MessageKey)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Standard or large map (empty-land starts only). */
function MapSizeField({ large, onChange }: { large: boolean; onChange: (large: boolean) => void }) {
  const t = useT();
  return (
    <div className="field">
      <span>{t('menu.mapSize')}</span>
      <span className="segmented">
        {[false, true].map((l) => (
          <button
            key={String(l)}
            type="button"
            className={l === large ? 'active' : ''}
            aria-pressed={l === large}
            onClick={() => onChange(l)}
          >
            {t(l ? 'menu.mapSize.large' : 'menu.mapSize.normal')}
          </button>
        ))}
      </span>
    </div>
  );
}

function SinglePage({
  onStart,
}: {
  onStart: (airportName: string, demo: boolean, large: boolean, theme: number) => void;
}) {
  const t = useT();
  const [name, setName] = useState(() => randomAirportName());
  const [demo, setDemo] = useState(false);
  const [large, setLarge] = useState(false);
  const [theme, setTheme] = useState(0);
  return (
    <form
      className="sheet-body"
      onSubmit={(e) => {
        e.preventDefault();
        onStart(name.trim() || randomAirportName(), demo, large && !demo, theme);
      }}
    >
      <p className="sheet-lead">{t('menu.singleLead')}</p>
      <ul className="features">
        <Feature icon="build">{t('menu.feature.build')}</Feature>
        <Feature icon="plane">{t('menu.feature.flights')}</Feature>
        <Feature icon="handling">{t('menu.feature.ops')}</Feature>
        <Feature icon="cloud">{t('menu.feature.weather')}</Feature>
      </ul>
      <AirportNameField value={name} onChange={setName} />
      <MapThemeField theme={theme} onChange={setTheme} />
      <StartKind demo={demo} onChange={setDemo} />
      {!demo && <MapSizeField large={large} onChange={setLarge} />}
      <button type="submit" className="btn primary xl start-game">
        <Icon name="play" size={18} />
        {t('menu.start')}
      </button>
    </form>
  );
}

function CoopPage({ onStart }: { onStart: (mode: StartMode) => void }) {
  const t = useT();
  const lang = useGame((s) => s.lang);
  const relayUrl = useGame((s) => s.relayUrl);
  const menuError = useGame((s) => s.menuError);
  const [roomCode, setRoomCode] = useState('');
  const [airportName, setAirportName] = useState(() => randomAirportName());
  const [demo, setDemo] = useState(false);
  const [large, setLarge] = useState(false);
  const [theme, setTheme] = useState(0);
  const [listed, setListed] = useState(true);

  const join = (e: FormEvent) => {
    e.preventDefault();
    const code = roomCode.trim().toUpperCase();
    if (code) onStart({ kind: 'join', relayUrl, roomCode: code });
  };

  return (
    <div className="sheet-body">
      {menuError && (
        <div className="banner error" role="alert">
          <Icon name="warning" size={18} />
          {translate(lang, menuError.key, menuError.params)}
        </div>
      )}
      <p className="sheet-lead">{t('menu.coopLead')}</p>
      <PlayingAs />
      <OpenRooms onJoin={(code) => onStart({ kind: 'join', relayUrl, roomCode: code })} />

      <div className="choice host-choice">
        <div>
          <strong>{t('menu.hostCoop')}</strong>
          <span className="muted small">{t('menu.hostSub')}</span>
        </div>
        <AirportNameField value={airportName} onChange={setAirportName} />
        <MapThemeField theme={theme} onChange={setTheme} />
        <StartKind demo={demo} onChange={setDemo} />
        {!demo && <MapSizeField large={large} onChange={setLarge} />}
        <label className="check-row">
          <input type="checkbox" checked={listed} onChange={(e) => setListed(e.target.checked)} />
          <span>
            {t('coop.public')}
            <span className="muted small"> · {t('coop.publicHint')}</span>
          </span>
        </label>
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            // Hosting comes with the full version (the free Android game); joining is free.
            if (locked()) {
              offerFullVersion('coop');
              return;
            }
            const name = airportName.trim() || randomAirportName();
            const save = newGameSave(name, demo, large, theme);
            onStart(
              save
                ? { kind: 'host', relayUrl, save, listed, fresh: true }
                : { kind: 'host', relayUrl, airportName: name, listed, fresh: true },
            );
          }}
        >
          <Icon name="users" size={18} />
          {t('menu.host')}
        </button>
      </div>

      <form className="choice" onSubmit={join}>
        <div>
          <strong>{t('menu.joinCoop')}</strong>
          <span className="muted small">{t('menu.joinSub')}</span>
        </div>
        <div className="join-row">
          <input
            value={roomCode}
            onChange={(e) => setRoomCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
            placeholder="ABCDE"
            maxLength={5}
            className="code-input"
            aria-label={t('menu.roomCode')}
            spellCheck={false}
          />
          <button type="submit" className="btn" disabled={roomCode.trim().length === 0}>
            <Icon name="link" size={18} />
            {t('menu.join')}
          </button>
        </div>
      </form>

      <details className="advanced">
        <summary>{t('menu.relayUrl')}</summary>
        <input
          value={relayUrl}
          onChange={(e) => {
            useGame.setState({ relayUrl: e.target.value });
            savePrefs({ ...useGame.getState(), relayUrl: e.target.value });
          }}
          spellCheck={false}
        />
        <p className="muted small">{t('menu.coopHint')}</p>
      </details>
    </div>
  );
}

/** The account name players see in co-op, with the player's title. */
function PlayingAs() {
  const t = useT();
  const account = useGame((s) => s.account);
  const [title, setTitle] = useState<string | null>(null);
  useEffect(() => {
    if (account) void titleOf(account.username).then(setTitle);
  }, [account]);
  if (!account) return <NameField />;
  return (
    <p className="playing-as small">
      <Icon name="users" size={14} />
      {t('coop.playingAs', { name: account.username })} <TitleChip title={title} />
    </p>
  );
}

/** Co-op rooms hosts opened to everyone: join with a click. */
function OpenRooms({ onJoin }: { onJoin: (code: string) => void }) {
  const t = useT();
  const [rooms, setRooms] = useState<OpenRoom[] | null>(null);
  const refresh = useCallback(() => {
    openRooms()
      .then(setRooms)
      .catch(() => setRooms([]));
  }, []);
  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 10_000);
    return () => window.clearInterval(id);
  }, [refresh]);
  return (
    <div className="choice open-rooms">
      <div className="open-rooms-head">
        <div>
          <strong>{t('coop.openRooms')}</strong>
          <span className="muted small">{t('coop.openRoomsSub')}</span>
        </div>
        <button type="button" className="icon-btn" title={t('coop.refresh')} onClick={refresh}>
          <Icon name="refresh" size={16} />
        </button>
      </div>
      {rooms && rooms.length === 0 && <p className="muted small">{t('coop.noRooms')}</p>}
      {rooms && rooms.length > 0 && (
        <ul className="room-list">
          {rooms.map((r) => (
            <li key={r.code}>
              <span className="room-text">
                <strong>{r.airportName || r.code}</strong>
                <span className="muted small">
                  {t('coop.roomLine', { host: r.host, players: r.players, max: r.max })}{' '}
                  <TitleChip title={r.title} />
                </span>
              </span>
              <button type="button" className="btn small" onClick={() => onJoin(r.code)}>
                <Icon name="link" size={14} />
                {t('menu.join')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SettingsPage() {
  return (
    <div className="sheet-body">
      <SettingsContent />
    </div>
  );
}

/**
 * The films behind the title screen: two video layers, the next film waiting loaded on the back
 * one and faded in as the front one ends.
 */
function TitleFilm({ onShot, onFail }: { onShot: (poster: string) => void; onFail: () => void }) {
  const a = useRef<HTMLVideoElement>(null);
  const b = useRef<HTMLVideoElement>(null);
  const layers = [a, b];
  const [front, setFront] = useState(0);
  /** The film on each layer. */
  const [shots, setShots] = useState<[number, number]>([0, 1]);
  const switching = useRef(false);
  /** Films that would not play: one is skipped, all of them and the stills come back. */
  const failures = useRef(0);

  useEffect(() => {
    a.current?.play().catch(() => undefined);
  }, []);
  // Browsers pause videos in a hidden tab: carry on when the player comes back.
  useEffect(() => {
    const resume = () => {
      if (document.visibilityState === 'visible')
        layers[front]?.current?.play().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', resume);
    return () => document.removeEventListener('visibilitychange', resume);
  });

  const next = (from: number) => {
    if (from !== front || switching.current) return;
    switching.current = true;
    const back = 1 - from;
    const video = layers[back]?.current;
    if (video) {
      video.currentTime = 0;
      video.play().catch(() => undefined);
    }
    setFront(back);
    onShot(filmPoster(shots[back]));
    window.setTimeout(
      () => {
        // The faded-out layer loads the film after the one now playing.
        setShots((s) => {
          const n: [number, number] = [s[0], s[1]];
          n[from] = (s[back] + 1) % FILMS.length;
          return n;
        });
        switching.current = false;
      },
      FILM_FADE * 1000 + 200,
    );
  };

  return (
    <div className="title-slides title-film" aria-hidden="true">
      {layers.map((ref, i) => (
        <video
          // biome-ignore lint/suspicious/noArrayIndexKey: the two layers are fixed
          key={i}
          ref={ref}
          src={filmSrc(shots[i])}
          poster={filmPoster(shots[i])}
          className={i === front ? 'shown' : ''}
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          onTimeUpdate={(e) => {
            const v = e.currentTarget;
            if (v.duration - v.currentTime < FILM_FADE) next(i);
          }}
          // The first film starts as soon as it can (a play() before it has loaded may not take).
          onCanPlay={(e) => {
            if (i === front && e.currentTarget.paused)
              e.currentTarget.play().catch(() => undefined);
          }}
          onEnded={() => next(i)}
          onError={() => {
            failures.current++;
            if (failures.current >= FILMS.length) onFail();
            else if (i === front) next(i);
            else
              setShots((s) => {
                const n: [number, number] = [s[0], s[1]];
                n[i] = (s[i] + 1) % FILMS.length;
                return n;
              });
          }}
        />
      ))}
    </div>
  );
}

const DESTINATIONS: MessageKey[] = [
  'city.istanbul',
  'city.london',
  'city.berlin',
  'city.dubai',
  'city.rome',
  'city.paris',
  'city.amsterdam',
  'city.vienna',
  'city.athens',
  'city.madrid',
  'city.baku',
  'city.doha',
];
const STATUSES: MessageKey[] = [
  'board.onTime',
  'board.boarding',
  'board.gateClosed',
  'board.departed',
];

/** A departures board in the corner, ticking over every few seconds like the real thing. */
function DeparturesBoard() {
  const t = useT();
  const lang = useGame((s) => s.lang);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((n) => n + 1), 4000);
    return () => window.clearInterval(id);
  }, []);
  const rows = Array.from({ length: 5 }, (_, i) => {
    const n = tick + i;
    const airline = AIRLINES[(n * 5) % AIRLINES.length];
    const minutes = 17 * 60 + 40 + n * 15;
    const time = `${String(Math.floor(minutes / 60) % 24).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
    return {
      key: n,
      time,
      flight: `${airline?.code ?? 'AM'} ${100 + ((n * 37) % 900)}`,
      dest: DESTINATIONS[(n * 7) % DESTINATIONS.length] ?? 'city.istanbul',
      status: STATUSES[Math.max(0, 3 - i)] ?? 'board.onTime',
    };
  });
  return (
    <aside className="board" aria-hidden>
      <header>
        <Icon name="plane" size={16} />
        <span>{t('board.departures')}</span>
      </header>
      <ul>
        {rows.map((r) => (
          <li key={r.key} className={r.status === 'board.boarding' ? 'boarding' : ''}>
            <span className="board-time">{r.time}</span>
            <span className="board-flight">{r.flight}</span>
            <span className="board-dest">{t(r.dest).toLocaleUpperCase(localeOf(lang))}</span>
            <span className="board-status">{t(r.status)}</span>
          </li>
        ))}
      </ul>
    </aside>
  );
}
